import { useState } from 'react';
import type { ReactElement } from 'react';
import { useTranslation } from 'react-i18next';

import { Alert } from '@/shared/ui/Alert';
import { Button } from '@/shared/ui/Button';
import { ConfirmDialog } from '@/shared/ui/ConfirmDialog';
import { Drawer } from '@/shared/ui/Drawer';
import { StatusBadge } from '@/shared/ui/StatusBadge';

import { statusLabelKey, statusTone } from '../model/access';

import type { Role, TenantUser } from '@/shared/api/generated/tenancy';

/* One person's access, and the two things an administrator can change about it.
 *
 * **Roles are a multi-select with one Save, not a row of grant and revoke buttons.** `PUT
 * .../roles` replaces the whole set and the server computes the difference, so "remove admin, add
 * reader" is one request. Per-role buttons would make it two, with an observable window in which
 * the user holds neither or both — and on a permissions surface that window is the whole problem.
 *
 * **Disabling is separated from the role editor and confirms in words.** It is the offboard, and
 * the sentence has to be accurate: the JWT is stateless with a one-hour TTL and there is no
 * denylist, so a disabled user's *current* session keeps working until it expires. "Access revoked"
 * would be a claim the backend does not make; "cannot sign in again" is what actually happens.
 *
 * **Deleting a user is now offered, and it is a soft delete — which is what changed rather than the
 * reasoning.** This paragraph used to say deletion was refused because the audit chain references
 * the subject for seven years and a removed credential row would leave entries naming somebody
 * nothing can resolve. That constraint has not moved; it is what the *form* of the delete is built
 * around. The credential is retired and its address permanently reserved, and the subject survives,
 * so every audit entry naming that person still resolves to them. A hard delete would still be
 * refused for exactly the reason the old paragraph gave.
 *
 * **The reservation is the consequence a reader will not expect, so it is the sentence that leads
 * the confirmation.** Cannot sign in and roles revoked are both true of disabling too; the address
 * being burned is the one thing that is irreversible and the one thing nobody guesses.
 *
 * **Delete is separated from Disable by its own section, its own heading and a different button
 * tone**, because the two now sit on one panel and a reader has to be able to tell "they might come
 * back" from "they are gone". Three separations rather than one: Disable stays a `secondary` button
 * under Access, Delete is a `danger` button under its own heading below a rule, and the two hints
 * say opposite things about reversibility in words — the enable hint already promises the same
 * password still works. Tone alone would not do it, since colour is exactly what a reader scanning
 * for the right control does not check.
 */

export interface UserDrawerProps {
  readonly user: TenantUser;
  readonly roles: readonly Role[];
  readonly callerSubject: string;
  /** False when the caller lacks the grant, or when this is their own account. */
  readonly mayDisable: boolean;
  /** Whether the caller holds `delete:identity.credential`.
   *
   * **Not narrowed for the caller's own account, unlike `mayDisable`.** The server answers 422 with
   * its own reason for a self-delete and for the last remaining admin, and those reasons are worth
   * reading: hiding the control would leave an administrator unable to tell whether they lacked the
   * permission or were simply refused, which is the same argument the approval panel makes about not
   * hiding an author's own signature button. */
  readonly mayDelete: boolean;
  readonly onClose: () => void;
  readonly onSaveRoles: (roleNames: readonly string[]) => void;
  readonly onSetDisabled: (disabled: boolean) => void;
  readonly onDelete: () => void;
  readonly saving: boolean;
  readonly error: Error | null;
  /** Kept apart from `saving`/`error` so a refused delete does not appear over the role editor and a
   * role save does not spin the delete button. Two independent actions, two independent states. */
  readonly deleting: boolean;
  readonly deleteError: Error | null;
}

export function UserDrawer({
  user,
  roles,
  callerSubject,
  mayDisable,
  mayDelete,
  onClose,
  onSaveRoles,
  onSetDisabled,
  onDelete,
  saving,
  error,
  deleting,
  deleteError,
}: UserDrawerProps): ReactElement {
  const { t } = useTranslation();
  const [selected, setSelected] = useState<readonly string[]>(user.roles);
  /* The confirmation's open state lives here rather than with the caller, so the drawer owns the
   * whole question. It stays open on a refusal — `ConfirmDialog` takes `error` for exactly that, and
   * its docstring records why: an earlier delete flow closed on refusal and put the reason on the
   * card underneath, which is somewhere other than where the question was asked. */
  const [confirmingDelete, setConfirmingDelete] = useState(false);

  const changed =
    selected.length !== user.roles.length ||
    selected.some((name) => !user.roles.includes(name));
  const isSelf = user.subject === callerSubject;

  return (
    <Drawer
      title={user.display_name}
      closeLabel={t('admin.drawer.close')}
      onClose={onClose}
      meta={
        <span className="lq-kpi__gap">
          <span className="lq-mono lq-muted">{user.subject}</span>
          <StatusBadge tone={statusTone(user)} label={t(statusLabelKey(user))} />
        </span>
      }
    >
      <h3 className="lq-section-header__title">{t('admin.drawer.roles')}</h3>
      <p className="lq-form-note">{t('admin.drawer.rolesHint')}</p>

      <div className="lq-stack">
        {roles.map((role) => (
          <label key={role.name} className="lq-checkbox">
            <input
              className="lq-checkbox__input"
              type="checkbox"
              checked={selected.includes(role.name)}
              onChange={(event) => {
                setSelected((current) =>
                  event.target.checked
                    ? [...current, role.name]
                    : current.filter((name) => name !== role.name),
                );
              }}
            />
            <span className="lq-checkbox__label">
              <span className="lq-mono">{role.name}</span>
              {role.description !== '' && (
                <span className="lq-muted"> — {role.description}</span>
              )}
            </span>
          </label>
        ))}
      </div>

      {selected.length === 0 && (
        /* Said before the save rather than after. An empty set is a legitimate thing to want — it
           is how you suspend somebody's access without disabling the account — but it denies
           everything, and an administrator who meant to swap one role for another should see that
           they have removed the last one. */
        <Alert tone="warning">{t('admin.drawer.noRolesWarning')}</Alert>
      )}

      {selected.includes('admin') && selected.includes('data-steward') && (
        /* Segregation of duties is a product feature, not a convention: a steward authors and an
           approver signs, and the platform refuses an author's own signature. Granting one person
           both roles does not break that refusal — the two-eyes check is per *subject* — but it
           does mean one person can author and then be the only plausible second signature, which
           is worth saying out loud before somebody does it by accident. */
        <Alert tone="warning">{t('admin.drawer.segregationWarning')}</Alert>
      )}

      {error !== null && (
        <Alert tone="error" title={t('admin.drawer.failed')}>
          {error.message}
        </Alert>
      )}

      <div className="lq-form-actions">
        <Button tone="secondary" onClick={onClose}>
          {t('common.cancel')}
        </Button>
        <Button
          tone="primary"
          busy={saving}
          disabled={!changed}
          onClick={() => {
            onSaveRoles(selected);
          }}
        >
          {t('admin.drawer.save')}
        </Button>
      </div>

      <hr className="lq-hr" />

      <h3 className="lq-section-header__title">{t('admin.drawer.access')}</h3>
      {isSelf ? (
        <p className="lq-form-note">{t('admin.drawer.cannotDisableSelf')}</p>
      ) : (
        <>
          <p className="lq-form-note">
            {user.disabled ? t('admin.drawer.enableHint') : t('admin.drawer.disableHint')}
          </p>
          <Button
            tone="secondary"
            busy={saving}
            disabled={!mayDisable && !user.disabled}
            onClick={() => {
              onSetDisabled(!user.disabled);
            }}
          >
            {user.disabled ? t('admin.drawer.enable') : t('admin.drawer.disable')}
          </Button>
        </>
      )}

      {mayDelete && (
        <>
          <hr className="lq-hr" />

          {/* Its own heading rather than a second button in the Access row. "Access" is about
              whether somebody can get in, which is a reversible question; this is about whether
              their account exists at all. */}
          <h3 className="lq-section-header__title">{t('admin.drawer.remove')}</h3>

          {/* **A strip, not an `Alert`.** This is a standing fact about how deletion works on this
              platform rather than something that has happened — the distinction `.lq-strip` exists
              for. An `Alert`'s heading over a permanent fact turns it into a notice a reader learns
              to skip, on the one sentence that says the address cannot be reused. */}
          <p className="lq-strip lq-strip--warn">
            <strong className="lq-strip__lead">{t('admin.drawer.deleteLead')}</strong>
            <span className="lq-strip__body">{t('admin.drawer.deleteBody')}</span>
          </p>

          {isSelf && (
            /* Said before the click rather than discovered after it, and the control stays live: the
               server's 422 names the reason, and a disabled button would leave the reader guessing
               whether they lacked the permission instead. */
            <p className="lq-form-note">{t('admin.drawer.deleteSelfNote')}</p>
          )}

          <div className="lq-form-actions">
            <Button
              tone="danger"
              busy={deleting}
              onClick={() => {
                setConfirmingDelete(true);
              }}
            >
              {t('admin.drawer.delete')}
            </Button>
          </div>

          <ConfirmDialog
            open={confirmingDelete}
            title={t('admin.drawer.confirmDeleteTitle')}
            /* A node rather than a string, so the person's own name and address carry the emphasis
               and the three consequences read as a list rather than as one long sentence. The
               reservation is last because it is the irreversible one and the one a reader is most
               likely to still be reading when they reach the buttons. */
            message={
              <>
                <p>
                  <strong>{user.display_name}</strong>{' '}
                  <span className="lq-mono lq-muted">{user.subject}</span>
                </p>
                <ul className="lq-consequences">
                  <li>{t('admin.drawer.consequence.signIn')}</li>
                  <li>{t('admin.drawer.consequence.roles')}</li>
                  <li>{t('admin.drawer.consequence.reserved')}</li>
                </ul>
                <p className="lq-form-note">{t('admin.drawer.consequence.audit')}</p>
              </>
            }
            confirmLabel={t('admin.drawer.confirmDelete')}
            cancelLabel={t('common.cancel')}
            busy={deleting}
            /* Rendered inside the dialog rather than behind it. Both server refusals — your own
               account, and the last remaining admin — arrive here with the server's own sentence,
               which is what makes them readable and actionable where the question was asked. */
            {...(deleteError === null ? {} : { error: deleteError.message })}
            onConfirm={onDelete}
            onCancel={() => {
              setConfirmingDelete(false);
            }}
          />
        </>
      )}
    </Drawer>
  );
}
